import React, { useEffect, useState } from 'react'
import { DatePicker } from 'antd'
import { DatePicker as MobileDatePicker } from 'antd-mobile'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import type { OccupiedPeriod } from '@/types/travel'

interface DateTimePickerProps {
  value?: string
  onChange: (value: string) => void
  label: string
  required?: boolean
  placeholder?: string
  compact?: boolean
  disabled?: boolean
  /** 最小可选日期（ISO 格式），用于结束日期不早于开始日期的约束 */
  minDate?: string
  /** 时间面板默认展示时间，格式 [hour, minute]，默认 [9, 0] */
  defaultTime?: [number, number]
}

/**
 * 判断结束时间选择器中某小时是否与已占用时段冲突（纯函数，便于单元测试）
 *
 * 置灰条件（开区间）：startConstraint < re AND current+H:59 > rs
 *
 * @param hour            待检测小时（0-23）
 * @param currentDate     结束时间选择器当前选中日期（yyyy-MM-dd 或包含时间的 ISO 字符串）
 * @param occupiedRanges  已占用时段列表
 * @param startConstraint 申请开始时间（ISO 字符串）
 */
export function isHourConflict(
  hour: number,
  currentDate: string,
  occupiedRanges: OccupiedPeriod[],
  startConstraint: string,
): boolean {
  if (!occupiedRanges || occupiedRanges.length === 0) return false

  const sc = dayjs(startConstraint)
  // 当前小时的末尾（H:59）
  const dateStr = currentDate.length > 10 ? currentDate.substring(0, 10) : currentDate
  const currentHourEnd = dayjs(`${dateStr}T${String(hour).padStart(2, '0')}:59`)

  return occupiedRanges.some(({ startDate, endDate }) => {
    const rs = dayjs(startDate)
    const re = dayjs(endDate)
    return sc.isBefore(re) && currentHourEnd.isAfter(rs)
  })
}

/**
 * 日期时间选择器组件
 * - 时间范围：00:00 - 23:45（全天）
 * - 时间间隔：每 30 分钟一档（0、30）
 * - 支持 minDate 约束：日历中早于 minDate 的日期置灰，同一天时早于 minDate 时间的时刻置灰
 */
const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  label,
  required = true,
  placeholder = '请选择日期和时间',
  compact = false,
  disabled = false,
  minDate,
  defaultTime = [9, 0],
}) => {
  const [narrowViewport, setNarrowViewport] = useState(false)
  const [mobilePickerVisible, setMobilePickerVisible] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 576px)')
    const apply = () => setNarrowViewport(mq.matches)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  const dayjsValue = value ? dayjs(value) : null
  const minDayjs = minDate ? dayjs(minDate) : null

  const displayFormat = compact
    ? 'MM-DD HH:mm'
    : narrowViewport
      ? 'YYYY-MM-DD HH:mm'
      : 'YYYY年MM月DD日 HH:mm'

  const handleChange = (date: Dayjs | null) => {
    if (date) {
      onChange(date.format('YYYY-MM-DDTHH:mm'))
    } else {
      onChange('')
    }
  }

  // 置灰早于 minDate 当天的所有日期
  const disabledDate = (current: Dayjs): boolean => {
    if (!minDayjs) return false
    return current.isBefore(minDayjs.startOf('day'))
  }

  // 禁用时间规则：全天 0-23 均可选，仅禁用早于 minDate 的时刻（同一天时）及冲突时段
  const disabledTime = (current: Dayjs | null) => {
    const isSameDay = !!(minDayjs && current && current.isSame(minDayjs, 'day'))
    const minHour = isSameDay ? minDayjs!.hour() : 0
    const minMinute = isSameDay ? minDayjs!.minute() : 0

    return {
      disabledHours: () => {
        const hours = new Set<number>()
        // 同一天时，禁用早于 minDate 的整点小时
        if (isSameDay) {
          for (let i = 0; i < minHour; i++) hours.add(i)
        }
        return Array.from(hours)
      },
      disabledMinutes: (selectedHour: number) => {
        const disabled = new Set<number>()
        for (let i = 0; i < 60; i++) {
          // 仅允许每 30 分钟整点（0、30）
          if (i % 30 !== 0) { disabled.add(i); continue }
          if (isSameDay && selectedHour === minHour && i < minMinute) {
            disabled.add(i)
          }
        }
        return Array.from(disabled)
      },
    }
  }

  return (
    <div className={compact ? 'datetime-picker-root datetime-picker-root--compact' : 'datetime-picker-root'}>
      {label && (
        <label className="heading-2 mb-clay-sm block">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* 移动端：使用 antd-mobile DatePicker */}
      {narrowViewport ? (
        <>
          <div
            className={`clay-input flex items-center justify-between ${
              disabled
                ? 'bg-gray-100 cursor-not-allowed opacity-60'
                : 'cursor-pointer'
            }`}
            style={{ height: compact ? '36px' : '44px' }}
            onClick={() => !disabled && setMobilePickerVisible(true)}
          >
            <span className={value ? 'text-slate-700 text-sm' : 'text-slate-400 text-sm'}>
              {value ? dayjs(value).format('YYYY-MM-DD HH:mm') : placeholder}
            </span>
            <div className="flex items-center gap-2">
              {value && !disabled && (
                <svg
                  className="w-4 h-4 text-slate-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  onClick={(e) => {
                    e.stopPropagation()
                    onChange('')
                  }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <MobileDatePicker
            visible={mobilePickerVisible}
            onClose={() => setMobilePickerVisible(false)}
            value={dayjsValue
              ? dayjsValue.toDate()
              : (minDayjs ?? dayjs()).hour(defaultTime[0]).minute(defaultTime[1]).second(0).toDate()
            }
            onConfirm={(val: Date) => {
              const selected = dayjs(val)

              // 检查是否早于 minDate（antd-mobile min 只约束日期，不约束时间）
              if (minDayjs && selected.isBefore(minDayjs)) {
                alert('所选时间不能早于开始时间，请重新选择')
                return
              }

              onChange(selected.format('YYYY-MM-DDTHH:mm'))
            }}
            min={minDayjs ? minDayjs.toDate() : undefined}
            precision="minute"
            filter={{
              minute: (val: number, { date }: { date: Date | null }) => {
                // 仅允许 30 分钟整点
                if (val % 30 !== 0) return false
                // minDate 同一天同一小时时，过滤早于 minDate 分钟的选项
                if (minDayjs && date) {
                  const d = dayjs(date)
                  if (d.isSame(minDayjs, 'day') && d.hour() === minDayjs.hour() && val < minDayjs.minute()) return false
                }
                return true
              },
              hour: (val: number, { date }: { date: Date | null }) => {
                // minDate 同一天时，过滤早于 minDate 的小时
                if (minDayjs && date) {
                  const d = dayjs(date)
                  if (d.isSame(minDayjs, 'day') && val < minDayjs.hour()) return false
                }
                return true
              }
            }}
          />
        </>
      ) : (
        /* 桌面端：使用 antd DatePicker */
        <DatePicker
          showTime={{
            format: 'HH:mm',
            hideDisabledOptions: true,
            defaultValue: dayjs().hour(defaultTime[0]).minute(defaultTime[1]).second(0),
          }}
          format={displayFormat}
          value={dayjsValue}
          onChange={handleChange}
          disabledDate={minDayjs ? disabledDate : undefined}
          disabledTime={disabledTime}
          placeholder={placeholder}
          className="w-full clay-input"
          style={{
            height: compact ? '36px' : '44px',
          }}
          showNow={false}
          classNames={{ popup: { root: 'responsive-datetime-popup' } }}
          disabled={disabled}
        />
      )}

      {label && (
        <p className="hint-text mt-1 leading-relaxed">
          {narrowViewport
            ? `00:00–23:30，每 30 分钟；默认 ${String(defaultTime[0]).padStart(2, '0')}:${String(defaultTime[1]).padStart(2, '0')}`
            : `可选时间：00:00 - 23:30，每 30 分钟一档，默认 ${String(defaultTime[0]).padStart(2, '0')}:${String(defaultTime[1]).padStart(2, '0')}`}
        </p>
      )}
    </div>
  )
}

export default DateTimePicker
